import { faCheck } from "@fortawesome/free-solid-svg-icons/faCheck";
import { faCircleNotch } from "@fortawesome/free-solid-svg-icons/faCircleNotch";
import { faGear } from "@fortawesome/free-solid-svg-icons/faGear";
import React, { useCallback, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";

import { AtiraDropdown } from "../../../../components/AtiraDropdown";
import { AtiraIcon } from "../../../../components/AtiraIcon";
import { TagDot } from "../../../../components/TagChip";
import { Text } from "../../../../components/Text";
import { TextWithTruncated } from "../../../../components/TextWithTruncated";
import { useAppSelector } from "../../../../redux/store";
import { tagSliceSelectors } from "../../../../redux/tag/tag.selector";
import { Spacing } from "../../../../theme/Spacing";
import { EntryTagsMenuPopup } from "../../../entries/components/EntryTagsMenuPopup";
import { useDrawersContext } from "../../../tasks/drawers-context";

// Plain elements rather than Flex, which keeps data attributes to itself
const Option = styled.div`
  display: flex;
  align-items: center;
  gap: ${Spacing.s};
  min-width: 0;
`;

const OptionEnd = styled.span`
  display: flex;
  margin-inline-start: auto;
  flex-shrink: 0;
`;

type WATagsDropdownProps = {
  selectedIds: string[];
  /** Locks the menu until the change has saved */
  onToggle: (tagId: string) => Promise<void>;
  testIds: {
    button: string;
    search: string;
    option: (name: string) => string;
  };
  children: React.ReactNode;
};

export const WATagsDropdown: React.FC<WATagsDropdownProps> = ({
  selectedIds,
  onToggle,
  testIds,
  children,
}) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [pendingTagId, setPendingTagId] = useState<string | null>(null);
  const keepOpenRef = useRef(false);

  const { t } = useTranslation();
  const { setTagsManageVisible } = useDrawersContext();

  const tags = useAppSelector(tagSliceSelectors.selectTags);

  const onTagClick = useCallback(
    async (tagId: string) => {
      keepOpenRef.current = true;

      if (pendingTagId) {
        return;
      }

      setPendingTagId(tagId);

      try {
        await onToggle(tagId);
      } finally {
        setPendingTagId(null);
      }
    },
    [onToggle, pendingTagId],
  );

  // Clicking a tag would close the menu, but several are often picked in a row
  const onOpenChange = useCallback(
    (next: boolean, info: { source: "trigger" | "menu" }) => {
      const keepOpen = keepOpenRef.current;
      keepOpenRef.current = false;

      if (!next && info.source === "menu" && keepOpen) {
        return;
      }

      setOpen(next);

      if (!next) {
        setSearch("");
      }
    },
    [],
  );

  const query = search.trim().toLowerCase();
  const matchingTags = tags.filter((tag) =>
    tag.name.toLowerCase().includes(query),
  );

  const items = [
    ...(matchingTags.length
      ? matchingTags.map((tag) => ({
          key: `tag-${tag._id}`,
          disabled: Boolean(pendingTagId),
          label: (
            <Option
              data-testid={testIds.option(tag.name)}
              data-selected={selectedIds.includes(tag._id)}
            >
              <TagDot $color={tag.color} />
              <TextWithTruncated fitToWidth color="main">
                {tag.name}
              </TextWithTruncated>
              <OptionEnd>
                {pendingTagId === tag._id ? (
                  <AtiraIcon
                    icon={faCircleNotch}
                    color="main"
                    animations={["spin"]}
                  />
                ) : selectedIds.includes(tag._id) ? (
                  <AtiraIcon icon={faCheck} color="main" />
                ) : null}
              </OptionEnd>
            </Option>
          ),
          onClick: () => onTagClick(tag._id),
        }))
      : [
          {
            key: "tags-empty",
            disabled: true,
            label: (
              <Text fontSize="s" color="darkerSub">
                {t(
                  tags.length
                    ? "entries.table.entry.tags.noMatch"
                    : "entries.table.entry.tags.empty",
                )}
              </Text>
            ),
          },
        ]),
    { type: "divider" as const, key: "tags-divider" },
    {
      key: "tags-manage",
      label: (
        <Option>
          <AtiraIcon icon={faGear} color="main" />
          <Text color="main">{t("entries.table.entry.tags.manage")}</Text>
        </Option>
      ),
      onClick: () => setTagsManageVisible(true),
    },
  ];

  return (
    <AtiraDropdown
      items={items}
      open={open}
      onOpenChange={onOpenChange}
      placement="bottomRight"
      trigger={["click"]}
      destroyOnHidden
      popupRender={(menu) => (
        <EntryTagsMenuPopup
          menu={menu}
          search={search}
          onSearch={setSearch}
          searchTestId={testIds.search}
        />
      )}
      dataTestId={testIds.button}
    >
      {children}
    </AtiraDropdown>
  );
};
